// Modal « Importer mes contacts (CSV) » — deux étapes strictes :
// 1) Upload du fichier (max 5 MB) → POST /leads-engine/user-import/upload
// 2) Bouton « Lancer le croisement » → déclenche unified-leads
//    avec la source USER_IMPORT active.
// Aucun mock ; les compteurs affichés proviennent 100% du backend.
import { useState } from "react";
import { X, Upload, Link2, CheckCircle } from "lucide-react";
import {
  NAVY, CHAMPAGNE, SLATE, IVORY, GOLD, GhostButton, PrimaryButton, Loader,
} from "@/components/altiora/PremiumUI";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function CsvImportModal({ open, onClose, onCrossReferenced }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(null);
  const [crossing, setCrossing] = useState(false);
  const [report, setReport] = useState(null);

  if (!open) return null;

  const reset = () => {
    setFile(null); setUploaded(null); setReport(null);
    setUploading(false); setCrossing(false);
  };
  const handleClose = () => { reset(); onClose?.(); };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/leads-engine/user-import/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(r.data);
      toast.success("CSV importé — prêt pour le croisement.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Échec de l'upload.";
      toast.error(msg);
    } finally { setUploading(false); }
  };

  const handleCross = async () => {
    setCrossing(true);
    try {
      const r = await api.post("/leads-engine/unified-leads", {
        territory: { label: "QC", provinces: ["QC"], cities: [],
                     audience_types: ["dirigeants_pme"],
                     offer_type: "b2b_services" },
        sources: ["USER_IMPORT", "REGISTRY_ISED",
                  "PROCUREMENT_SEAO", "NEWS_CNW"],
        limit: 10, max_per_source: 1500,
      }, { timeout: 180000 });
      setReport(r.data);
      onCrossReferenced?.(r.data);
      toast.success("Croisement terminé.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Échec du croisement.";
      toast.error(msg);
    } finally { setCrossing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         data-testid="csv-import-modal">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose}
           data-testid="csv-modal-backdrop" />
      <div className="relative w-full max-w-lg rounded-xl shadow-2xl overflow-hidden"
           style={{ background: IVORY }}>
        <header className="px-6 py-4 flex items-center gap-3"
                style={{ background: NAVY, color: IVORY }}>
          <Upload className="h-4 w-4" style={{ color: GOLD }} />
          <h3 className="font-serif text-[16px] flex-1">
            Importer mes contacts (CSV)
          </h3>
          <button onClick={handleClose} data-testid="csv-modal-close"
                  className="p-1 rounded hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="p-6 space-y-5">
          <p className="text-[12px]" style={{ color: SLATE }}>
            Colonnes supportées : company_name, business_number, city, province,
            contact_name, contact_role, contact_email, contact_phone, notes,
            source_note. Fichier max 5 MB.
          </p>

          {/* Étape 1 — upload */}
          <div className="rounded-lg p-4 bg-white"
               style={{ border: `1px solid ${CHAMPAGNE}44` }}>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] mb-2"
                 style={{ color: NAVY }}>
              1 · Sélectionner le fichier
            </div>
            <input type="file" accept=".csv,text/csv"
                   data-testid="csv-file-input"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   className="text-[13px]" />
            {file && (
              <div className="mt-2 text-[12px]" style={{ color: SLATE }}>
                {file.name} · {(file.size / 1024).toFixed(1)} Ko
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <PrimaryButton onClick={handleUpload}
                             disabled={!file || uploading || !!uploaded}
                             testid="csv-upload-btn">
                {uploading ? "Envoi…" : uploaded ? "Envoyé" : "Envoyer le CSV"}
              </PrimaryButton>
              {uploaded && (
                <span className="flex items-center gap-1 text-[12px]"
                      style={{ color: "#1F8F5B" }}>
                  <CheckCircle className="h-3 w-3" />
                  {uploaded.bytes} octets stockés.
                </span>
              )}
            </div>
          </div>

          {/* Étape 2 — cross-référence */}
          <div className="rounded-lg p-4 bg-white"
               style={{ border: `1px solid ${CHAMPAGNE}44`,
                        opacity: uploaded ? 1 : 0.5 }}>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] mb-2"
                 style={{ color: NAVY }}>
              2 · Croiser avec les signaux publics
            </div>
            <p className="text-[12px]" style={{ color: SLATE }}>
              ALTIORA va comparer vos contacts avec ISED, SEAO et CNW pour
              identifier les entités actives, les décideurs connus et les
              signaux commerciaux récents.
            </p>
            <div className="mt-3">
              <PrimaryButton onClick={handleCross}
                             disabled={!uploaded || crossing}
                             testid="csv-cross-btn">
                <Link2 className="h-3 w-3 inline mr-1" />
                {crossing ? "Croisement en cours…" : "Lancer le croisement"}
              </PrimaryButton>
              {crossing && (
                <div className="mt-3"><Loader label="Analyse multi-sources…" /></div>
              )}
            </div>
          </div>

          {/* Rapport */}
          {report && (
            <div data-testid="csv-import-report"
                 className="rounded-lg p-4"
                 style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}55` }}>
              <div className="text-[11px] font-mono uppercase tracking-[0.22em] mb-2"
                   style={{ color: NAVY }}>
                Résultats du croisement
              </div>
              <ul className="text-[12px] space-y-1" style={{ color: NAVY }}>
                <li>Entités analysées : {report.counters?.entities ?? 0}</li>
                <li>Résolues sur plusieurs sources : {report.counters?.cross_source_resolved ?? 0}</li>
                <li>Signaux récents : {report.counters?.with_recent_signal ?? 0}</li>
                <li>Décideurs identifiés : {report.counters?.decision_makers_identified ?? 0}</li>
                <li>Leads Tier A : {report.counters?.tier_a ?? 0} · Tier B : {report.counters?.tier_b ?? 0}</li>
                <li>Leads contactables : {report.counters?.contactable_leads ?? 0}</li>
              </ul>
              <div className="mt-3">
                <GhostButton onClick={handleClose} testid="csv-report-close">
                  Fermer et voir les {report.count} leads
                </GhostButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
